import { PlusIcon } from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PEOPLE_FORM_DEFAULT_VALUES } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitPeople/constants';
import { TBroadcastScheduleProgramBodySegmentToolkitPeopleForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitPeople/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { usePostClipPeople } from '@/features/clips/api/post-clips-people';
import { TPatchClipPeople, TPostClipPeople } from '@/features/clips/types';
import { usePostFeaturedPeople } from '@/features/featured-people/api/post-featured-people';
import { TFeaturedPeopleForm, TFeaturedPeopleType } from '@/features/featured-people/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

export function useClipToolkitPeopleFormState() {
  const {
    personaClipData: peopleData,
    drawerRect,
    activeClip,
    formBox,
    playerVideoCurrentTime,
    getDateTimeCurrentClip,
    setPersonaClipData,
    setFormBox,
    setActiveClip,
    setVisibleBox,
    drawnFormReset,
    setClipItems,
  } = useBroadcastScheduleProgram();
  const postClipPeople = usePostClipPeople();
  const patchClipPeople = usePatchClipPeople();
  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitPeopleForm>({
    mode: 'all',
    defaultValues: PEOPLE_FORM_DEFAULT_VALUES,
  });
  const peopleDrawer = useDisclosureWithData();
  const { t } = useTranslation();
  const { mutateAsync: createPeople, isPending: isCreatingPeople } = usePostFeaturedPeople();

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nueva persona de interés'),
        icon: PlusIcon,
        onClick: () => peopleDrawer.open(),
      },
    ];
  };

  const handleSubmitPeople = useCallback(
    async (data: TFeaturedPeopleForm): Promise<TFeaturedPeopleType> => {
      try {
        const result = await createPeople(data);
        peopleDrawer.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createPeople]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitPeopleForm) => {
    const start_datetime =
      data.start_datetime ?? getDateTimeCurrentClip(playerVideoCurrentTime) ?? null;
    const formatedData: TPostClipPeople = {
      ...data,
      start_datetime,
      end_datetime: data.end_datetime ?? start_datetime,
      parameters: {
        ...data.parameters,
        bbox: data.parameters.bbox || [0.045, 0.075, 0.96, 0.925], // Full screen
      },
      clip_id: activeClip?.id || '',
      entity_score: Number(data.entity_score) / 100,
    };
    if (peopleData?.id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostClipPeople) => {
    try {
      const item = await postClipPeople.mutateAsync(formatedData);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_peoples: [...(prev.clip_peoples || []), item],
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return { ...clip, clip_peoples: [...(clip.clip_peoples || []), item] };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el people:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostClipPeople) => {
    const { clip_id, ...restData } = formatedData;
    const body: TPatchClipPeople = {
      id: peopleData?.id || '',
      ...restData,
    };
    try {
      const item = await patchClipPeople.mutateAsync(body);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_peoples: prev.clip_peoples?.map(people => {
              if (people.id === item.id) {
                return item;
              }
              return people;
            }),
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === clip_id) {
            return {
              ...clip,
              clip_peoples: (clip.clip_peoples || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar la persona de interés:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(PEOPLE_FORM_DEFAULT_VALUES);
    setPersonaClipData(null);
    drawnFormReset();
  }, [setFormBox, setPersonaClipData]);

  useEffect(() => {
    if (drawerRect && formBox === 'people') {
      form.setValue('parameters.bbox', drawerRect);
    }
  }, [drawerRect]);

  useEffect(() => {
    if (formBox !== 'people') return;

    if (peopleData?.id) {
      form.setValue('people_id', peopleData.people_id);
      form.setValue('parameters.bbox', peopleData.parameters.bbox);
      form.setValue('start_datetime', new Date(peopleData.start_datetime));
      form.setValue('end_datetime', new Date(peopleData.end_datetime || peopleData.start_datetime));
      form.setValue('entity_score', peopleData.entity_score * 100);
      form.setValue('review_state', peopleData.review_state);
      form.setValue('parameters.visibility', peopleData.parameters.visibility);

      if (!activeClip || !peopleData.start_datetime) return;

      const seconds = convertISOToSeconds(
        peopleData.start_datetime,
        new Date(activeClip.start_datetime)
      );

      setVisibleBox({
        bbox: peopleData?.parameters.bbox || [0, 0, 0, 0],
        frame: seconds,
        entity_label: peopleData?.entity_label || '',
      });
    }
  }, [peopleData]);

  return {
    form,
    isCreatingPeople,
    peopleDrawer,
    renderAdditionalButtons,
    handleSubmitPeople,
    handleSubmit,
    handleClickExit,
  };
}
